import { Spinner } from '@lensora/ui'
import confetti from 'canvas-confetti'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { ComposerBar } from './ComposerBar'
import { TranscriptTurn } from './TranscriptTurn'
import { clearLegacyOnboardingState, clearOnboardingState, consumePendingWebsite, loadOnboardingState, saveOnboardingState } from './persistence'
import {
  ApiError,
  useAddCompetitorMutation,
  useCompleteOnboardingMutation,
  useCreateManagedWebsiteMutation,
  useCreateWebsiteMutation,
  useRunSimulationMutation,
  useSaveContextMutation,
  useScanPolling,
  useSuggestPromptsMutation,
} from './useOnboardingData'
import { onboardingReducer, createInitialMachineState } from './useOnboardingMachine'

import { useAuth } from '@/providers/auth-provider'

function guessCompetitorDomain(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9.]/g, '') + (name.includes('.') ? '' : '.com')
}

export interface OnboardingStatus {
  onboarding_completed: boolean
  has_website: boolean
  website_id: string | null
  industry: string | null
  brand_name: string | null
  niche_description: string | null
  suggested_prompts: string[]
  scan: { status: string } | null
  can_create_clients: boolean
}

interface OnboardingChatProps {
  firstName: string
  status: OnboardingStatus
  onComplete: () => void
}

export function OnboardingChat({ firstName, status, onComplete }: OnboardingChatProps) {
  const navigate = useNavigate()
  const auth = useAuth()
  const profileId = auth.status === 'authenticated' ? auth.user.id : null

  const [state, dispatch] = React.useReducer(
    onboardingReducer,
    { firstName, canCreateClients: status.can_create_clients },
    ({ firstName: fn, canCreateClients }) => createInitialMachineState(fn, canCreateClients)
  )
  const transcriptRef = React.useRef<HTMLDivElement>(null)
  const focusRef = React.useRef<HTMLInputElement>(null)
  const userScrolledUp = React.useRef(false)

  const createWebsite = useCreateWebsiteMutation()
  const createManagedWebsite = useCreateManagedWebsiteMutation()
  const saveContext = useSaveContextMutation()
  const suggestPrompts = useSuggestPromptsMutation()
  const completeOnboarding = useCompleteOnboardingMutation()
  const runSimulation = useRunSimulationMutation()
  const addCompetitor = useAddCompetitorMutation()

  // Lifted turn-local state so the persistent composer bar (below) can feed
  // the same state the inline per-turn widgets read/write — one source of
  // truth, no forked submit paths.
  const [contextNiche, setContextNiche] = React.useState('')
  const [promptsSelected, setPromptsSelected] = React.useState<string | null>(null)
  const [composerValue, setComposerValue] = React.useState('')
  const [competitorExtras, setCompetitorExtras] = React.useState<string[]>([])
  // Defense-in-depth against a same-session re-render double-firing the
  // running_demo effect — checked alongside state.step in the effect's guard,
  // set synchronously in the same place MARK_DEMO_DISPATCHED is dispatched.
  const hasFiredDemoRef = React.useRef(false)

  React.useEffect(() => { setContextNiche(state.answers.nicheDescription ?? '') }, [state.answers.nicheDescription])
  React.useEffect(() => { setPromptsSelected(null) }, [state.step === 'suggesting_prompts'])

  // Single-select: clicking a different chip always replaces the selection;
  // clicking the already-selected chip deselects it.
  function togglePrompt(p: string) {
    setPromptsSelected((prev) => (prev === p ? null : p))
  }

  function addCustomPrompt(p: string) {
    setPromptsSelected(p)
  }

  // One-time migration + restore on mount, scoped to the current user. Falls
  // back to server-reported status when nothing is persisted locally, so a
  // partially-completed account (e.g. website added from another device)
  // doesn't have to repeat the website step.
  React.useEffect(() => {
    clearLegacyOnboardingState()
    if (!profileId) return
    const saved = loadOnboardingState(profileId)
    if (saved && saved.state !== 'greeting' && saved.state !== 'asking_website') {
      dispatch({ type: 'RESTORE', step: saved.state, websiteId: saved.websiteId, answers: saved.answers })
      return
    }
    if (status.has_website && status.website_id && status.industry) {
      dispatch({
        type: 'RESTORE',
        step: 'suggesting_prompts',
        websiteId: status.website_id,
        answers: { industry: status.industry, nicheDescription: status.niche_description ?? undefined },
      })
    }
  }, [profileId])

  React.useEffect(() => {
    if (!profileId || state.step === 'done') return
    saveOnboardingState(profileId, { state: state.step, websiteId: state.websiteId, answers: state.answers })
  }, [profileId, state.step, state.websiteId, state.answers])

  React.useEffect(() => {
    if (state.step !== 'asking_website') return
    const pending = consumePendingWebsite()
    if (pending) dispatch({ type: 'RESTORE', step: 'asking_website', websiteId: state.websiteId, answers: { ...state.answers, websiteUrl: pending } })
  }, [])

  useScanPolling(
    state.websiteId,
    state.step === 'scanning',
    (scan) => dispatch({ type: 'SCAN_UPDATE', scan }),
    () => dispatch({ type: 'SCAN_TIMEOUT' })
  )

  React.useEffect(() => {
    if (state.step !== 'suggesting_prompts' || state.answers.suggestedPrompts !== undefined) return
    suggestPrompts.mutate(
      { website_id: state.websiteId ?? undefined, niche_description: state.answers.nicheDescription },
      {
        onSuccess: (d) => dispatch({ type: 'PROMPTS_LOADED', prompts: d.suggestions ?? [], usedAi: !!d.used_ai }),
        onError: (err) => {
          const kind = err instanceof ApiError && err.code === 'INSUFFICIENT_CREDITS' ? 'insufficient_credits' : 'generic'
          dispatch({ type: 'PROMPTS_ERROR', kind })
        },
      }
    )
  }, [state.step])

  // Live single-platform demo — fires once when the user picks a platform.
  // Best-effort: a failed run (out of credits right after signup, a provider
  // error) must never block onboarding from completing, it just skips the
  // live preview — the selected prompt is still available on the Prompts
  // page either way.
  React.useEffect(() => {
    if (state.step !== 'running_demo' || !state.answers.demoPrompt || !state.answers.demoPlatform) return
    if (hasFiredDemoRef.current) return
    // Hard security requirement: MARK_DEMO_DISPATCHED must be dispatched
    // synchronously, as the first line of this effect, before mutate() is
    // called — not in a callback, not after an await, not in onSuccess/
    // onSettled. This is what lets RESTORE's credit-double-spend guard tell
    // "a demo request may already have spent a credit" apart from "the user
    // never got past picking a platform" on a reload racing this request.
    hasFiredDemoRef.current = true
    dispatch({ type: 'MARK_DEMO_DISPATCHED' })
    runSimulation.mutate(
      {
        queries: [{ query_text: state.answers.demoPrompt }],
        platforms: [state.answers.demoPlatform],
        website_id: state.websiteId ?? undefined,
      },
      {
        onSuccess: (d) => {
          const result = d.result_details?.[0]
          if (!result) {
            dispatch({ type: 'DEMO_FAILED', retried: !!state.answers.demoRetried })
            return
          }
          dispatch({ type: 'DEMO_RESULT', result })
        },
        onError: () => dispatch({ type: 'DEMO_FAILED', retried: !!state.answers.demoRetried }),
      }
    )
  }, [state.step])

  React.useEffect(() => {
    if (state.step !== 'choosing_platform') return
    hasFiredDemoRef.current = false
  }, [state.step])

  React.useEffect(() => {
    if (state.step !== 'finishing') return
    async function run() {
      // Selected-but-not-demoed prompts are NOT bulk-added to the tracked
      // list here — they land in the Prompts page's "For You" suggestions
      // instead (website.suggestedPrompts was already persisted by the
      // /suggest-prompts call above, independent of what got selected here),
      // so the user picks which ones to actually track rather than every
      // suggestion getting force-added on their behalf.
      const names = state.answers.confirmedCompetitors ?? []
      if (names.length > 0 && state.websiteId) {
        const websiteId = state.websiteId
        await Promise.allSettled(
          names.map((name) =>
            addCompetitor.mutateAsync({ websiteId, name, url: `https://${guessCompetitorDomain(name)}` }).catch((err) => {
              // DUPLICATE_URL means the competitor is already tracked — that's
              // success from the user's point of view, not a failure to swallow
              // silently as an error. Any other failure must not block completion.
              if (err instanceof ApiError && err.code === 'DUPLICATE_URL') return
              throw err
            })
          )
        )
      }
      await completeOnboarding.mutateAsync().catch(() => {})
      if (profileId) clearOnboardingState(profileId)
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ['#6366f1', '#8b5cf6', '#3b82f6', '#ffffff'] })
      dispatch({ type: 'FINISHED' })
      onComplete()
      navigate('/dashboard/prompts')
    }
    void run()
  }, [state.step])

  React.useEffect(() => {
    if (userScrolledUp.current) return
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' })
    focusRef.current?.focus()
  }, [state.turns.length])

  function submitWebsite(url: string, clientName?: string) {
    dispatch({ type: 'SUBMIT_WEBSITE', url, clientName })
    const onSettled = {
      onSuccess: (d: { website_id: string; scan_dispatched: boolean }) =>
        dispatch({ type: 'WEBSITE_SAVED', websiteId: d.website_id, scanDispatched: d.scan_dispatched }),
      onError: (err: unknown) =>
        dispatch({ type: 'WEBSITE_FAILED', message: err instanceof ApiError ? err.message : 'Failed to add website' }),
    }
    // Separate route/hook rather than a branching URL inside one mutation —
    // the managed path takes a required client_name and returns a client_id,
    // a distinct enough contract that folding it into one hook would mean
    // conditional required fields on every caller.
    if (clientName) {
      createManagedWebsite.mutate({ url, client_name: clientName }, onSettled)
    } else {
      createWebsite.mutate({ url }, onSettled)
    }
  }

  function submitContext(industry: string, niche: string, accepted: boolean) {
    if (!state.websiteId) return
    saveContext.mutate({ website_id: state.websiteId, industry, niche_description: niche || undefined, accepted_suggestion: accepted })
    dispatch({ type: 'SUBMIT_CONTEXT', industry, nicheDescription: niche, acceptedSuggestion: accepted })
  }

  const composerPending =
    createWebsite.isPending || createManagedWebsite.isPending || suggestPrompts.isPending || completeOnboarding.isPending
  const composerDisabled =
    composerPending ||
    state.step === 'scanning' ||
    state.step === 'choosing_platform' ||
    state.step === 'running_demo' ||
    state.step === 'reviewing_response' ||
    state.step === 'finishing' ||
    state.step === 'done'

  const composerPlaceholder = (() => {
    switch (state.step) {
      case 'asking_website':
        return 'Type your website…'
      case 'confirming_context':
      case 'asking_context_manual':
        return 'Add anything else…'
      case 'suggesting_prompts':
        return 'Type a custom prompt…'
      case 'scanning':
        return 'Scanning your website…'
      case 'choosing_platform':
        return 'Use the buttons above…'
      case 'running_demo':
        return 'Running your prompt…'
      case 'reviewing_response':
        return 'Use the button above…'
      case 'confirming_competitors':
        return 'Add another competitor…'
      default:
        return ''
    }
  })()

  function submitComposer() {
    const text = composerValue.trim()
    if (!text) return
    switch (state.step) {
      case 'asking_website':
        submitWebsite(text)
        break
      case 'confirming_context':
      case 'asking_context_manual':
        // Populates the niche textarea inline, same as if the user had typed
        // there directly — does not bypass the reducer's explicit-confirm
        // requirement (SUBMIT_CONTEXT is still only dispatched from an
        // industry-chip + Continue click in ContextTurn).
        setContextNiche(text)
        break
      case 'suggesting_prompts':
        addCustomPrompt(text)
        break
      case 'confirming_competitors':
        setCompetitorExtras((prev) => [...prev, text])
        break
      default:
        return
    }
    setComposerValue('')
  }

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col pt-6">
      <div
        ref={transcriptRef}
        role="log"
        aria-live="polite"
        className="flex-1 space-y-4 overflow-y-auto pb-4"
        onScroll={(e) => {
          const el = e.currentTarget
          userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > 80
        }}
      >
        {state.turns.map((turn) => (
          <TranscriptTurn
            key={turn.id}
            turn={turn}
            answers={state.answers}
            websiteError={state.error}
            websiteSaving={createWebsite.isPending || createManagedWebsite.isPending}
            canCreateClients={status.can_create_clients}
            contextSaving={saveContext.isPending}
            contextError={null}
            promptsLoading={suggestPrompts.isPending}
            promptsErrorKind={state.promptsError}
            promptsSaving={completeOnboarding.isPending}
            onSubmitWebsite={submitWebsite}
            onSubmitContext={submitContext}
            onSubmitPrompts={(selected) => dispatch({ type: 'SELECT_DEMO_PROMPT', prompt: selected })}
            onSkipPrompts={() => dispatch({ type: 'SKIP_PROMPTS' })}
            onRetryPrompts={() => dispatch({ type: 'RESTORE', step: 'suggesting_prompts', websiteId: state.websiteId, answers: { ...state.answers, suggestedPrompts: undefined } })}
            onSelectPlatform={(platform) => dispatch({ type: 'SELECT_DEMO_PLATFORM', platform })}
            platformSaving={runSimulation.isPending}
            onContinueToCompetitors={() => dispatch({ type: 'CONTINUE_TO_COMPETITORS' })}
            onRetryDemo={() =>
              dispatch({
                type: 'RESTORE',
                step: 'choosing_platform',
                websiteId: state.websiteId,
                answers: { ...state.answers, demoPlatform: undefined, demoResult: undefined, demoDispatchedAt: undefined },
              })
            }
            onConfirmCompetitors={(names) => {
              setCompetitorExtras([])
              dispatch({ type: 'CONFIRM_COMPETITORS', names })
            }}
            competitorsSaving={false}
            focusRef={turn.component === 'website' ? focusRef : undefined}
            contextNiche={contextNiche}
            onContextNicheChange={setContextNiche}
            promptsSelected={promptsSelected}
            onTogglePrompt={togglePrompt}
            onAddCustomPrompt={addCustomPrompt}
            competitorExtras={competitorExtras}
          />
        ))}
        {state.step === 'finishing' && (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner size="sm" /> Finishing up…
          </div>
        )}
      </div>
      {state.step !== 'done' && (
        <ComposerBar
          value={composerValue}
          onChange={setComposerValue}
          onSubmit={submitComposer}
          placeholder={composerPlaceholder}
          disabled={composerDisabled}
          autoFocusToken={state.step === 'asking_website' ? '' : state.step}
        />
      )}
    </div>
  )
}
